/* eslint-disable */
// Generated from voia-travel-mobile app/utils/itineraryGlobeModel.ts (types stripped). Keep in sync: edit there, re-run the transpile.
/**
 * Everything the full-screen itinerary globe (map-itineraries) draws, built
 * from a raw trip doc: numbered stays, day visits, every commute leg (all
 * modes) and the route lines between them. Works on member trips, public
 * copies and shared links (whose dates sit on the 1900 flexible anchor).
 */
import { enrichItineraryMapPins } from './itineraryMapData.js';
import { buildRouteLines } from './itineraryRouteLines.js';
import { FLEXIBLE_TRIP_ANCHOR } from './tripDates.js';
const MODES = ['flight', 'train', 'boat', 'bus', 'car'];
const hasCoords = (p) => !!p && typeof p.latitude === 'number' && typeof p.longitude === 'number';
/** Great-circle distance in km between two points. */
const distanceKm = (a, b) => {
    const rad = Math.PI / 180;
    const dLat = (b.latitude - a.latitude) * rad;
    const dLng = (b.longitude - a.longitude) * rad;
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
};
/** How far a stay or place may sit from its stop and still belong to it (a city and its outskirts). */
const STOP_RADIUS_KM = 60;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const tripDay = (d, start) => Math.round((startOfDay(d) - startOfDay(start)) / 86400000) + 1;
/** "YYYY-MM-DD" → local date at noon (commute dates are calendar dates). */
const parseYmd = (s) => {
    if (typeof s !== 'string')
        return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m)
        return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
};
/** "Lisbon Airport (LIS), Lisbon, Portugal" → "Lisbon Airport (LIS)". */
const shortPlaceName = (name) => typeof name === 'string' ? name.split(',')[0].trim() : '';
const parseUtcHours = (utc) => {
    if (!utc)
        return null;
    const m = /^([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(utc.trim());
    if (!m)
        return null;
    const hours = Number(m[2]) + (m[3] ? Number(m[3]) / 60 : 0);
    return m[1] === '-' ? -hours : hours;
};
const instantMs = (t) => {
    var _a;
    if (!t.date || !t.time)
        return null;
    const [h, min] = t.time.split(':').map(Number);
    if (isNaN(h) || isNaN(min))
        return null;
    const offset = (_a = parseUtcHours(t.utc)) !== null && _a !== void 0 ? _a : 0;
    const utcMs = Date.UTC(t.date.getFullYear(), t.date.getMonth(), t.date.getDate(), h, min);
    return utcMs - offset * 3600000;
};
/** Initial compass bearing from a to b ([lng, lat]), degrees clockwise from north. */
export function bearing(a, b) {
    const rad = Math.PI / 180;
    const lat1 = a[1] * rad;
    const lat2 = b[1] * rad;
    const dLng = (b[0] - a[0]) * rad;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) / rad + 360) % 360;
}
/**
 * Great-circle path between two [lng, lat] points. Longitudes are unwrapped
 * (may leave [-180, 180]) so a flight across the antimeridian doesn't draw
 * the long way round the globe.
 */
export function greatCircle(from, to, steps = 64) {
    const rad = Math.PI / 180;
    const [lng1, lat1] = [from[0] * rad, from[1] * rad];
    const [lng2, lat2] = [to[0] * rad, to[1] * rad];
    const d = 2 * Math.asin(Math.sqrt(Math.sin((lat2 - lat1) / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2));
    if (d < 1e-6)
        return [from, to];
    const points = [];
    for (let i = 0; i <= steps; i++) {
        const f = i / steps;
        const a = Math.sin((1 - f) * d) / Math.sin(d);
        const b = Math.sin(f * d) / Math.sin(d);
        const x = a * Math.cos(lat1) * Math.cos(lng1) + b * Math.cos(lat2) * Math.cos(lng2);
        const y = a * Math.cos(lat1) * Math.sin(lng1) + b * Math.cos(lat2) * Math.sin(lng2);
        const z = a * Math.sin(lat1) + b * Math.sin(lat2);
        let lng = Math.atan2(y, x) / rad;
        const lat = Math.atan2(z, Math.sqrt(x * x + y * y)) / rad;
        const prev = points[points.length - 1];
        if (prev) {
            while (lng - prev[0] > 180)
                lng -= 360;
            while (lng - prev[0] < -180)
                lng += 360;
        }
        points.push([lng, lat]);
    }
    return points;
}
/**
 * Bow a path sideways, to the right of its direction of travel (negative
 * `amount` bows left). `amount` is the peak offset as a share of the
 * path's length; ends stay put. Works in a local flat projection, which is
 * plenty for separating lines that would otherwise overlap.
 */
export function bendPath(path, amount, steps = 32) {
    if (path.length < 2 || amount === 0)
        return path;
    // Ground legs are a single segment: give them points to curve through.
    const base = path.length > 2
        ? path
        : Array.from({ length: steps + 1 }, (_, i) => {
            const f = i / steps;
            return [path[0][0] + (path[1][0] - path[0][0]) * f, path[0][1] + (path[1][1] - path[0][1]) * f];
        });
    const a = base[0];
    const b = base[base.length - 1];
    const k = Math.cos(((a[1] + b[1]) / 2) * (Math.PI / 180)) || 1e-6;
    const dx = (b[0] - a[0]) * k;
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-9)
        return base;
    // Right of travel, with x east and y north.
    const nx = dy / len;
    const ny = -dx / len;
    const n = base.length - 1;
    return base.map(([lng, lat], i) => {
        const offset = amount * len * Math.sin((Math.PI * i) / n);
        return [lng + (nx * offset) / k, lat + ny * offset];
    });
}
/** Peak sideways bow for each extra line sharing both ends, as a share of length. */
const BEND = 0.12;
export function buildItineraryGlobeModel(tripData, savedPlaces = []) {
    const { pins, dayVisitAssociations } = enrichItineraryMapPins(tripData);
    const spine = pins
        .filter((p) => p.type !== 'dayVisit')
        .sort((a, b) => new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime());
    const tripStart = spine[0] ? new Date(spine[0].checkInDate) : new Date(FLEXIBLE_TRIP_ANCHOR);
    const flexible = tripStart.getFullYear() === FLEXIBLE_TRIP_ANCHOR.getFullYear();
    const stops = spine
        .filter((p) => p.type !== 'transit' && hasCoords(p))
        .map((p, i) => {
        const start = new Date(p.checkInDate);
        const end = new Date(p.checkOutDate);
        return {
            id: p.id,
            name: p.name || `Stop ${i + 1}`,
            latitude: p.latitude,
            longitude: p.longitude,
            number: i + 1,
            start,
            end,
            nights: Math.max(0, tripDay(end, start) - 1),
            dayStart: tripDay(start, tripStart),
            dayEnd: tripDay(end, tripStart),
            dayVisitIds: [],
            accommodationIds: [],
            placeIds: [],
        };
    });
    const stopById = new Map(stops.map((s) => [s.id, s]));
    const nearestStop = (p) => {
        let best = null;
        let bestKm = STOP_RADIUS_KM;
        stops.forEach((st) => {
            const km = distanceKm(p, st);
            if (km <= bestKm) {
                best = st;
                bestKm = km;
            }
        });
        return best;
    };
    // Accommodations sit under the stay they were booked for (pinId), else
    // the nearest stop.
    const accommodations = (Array.isArray(tripData === null || tripData === void 0 ? void 0 : tripData.accommodations) ? tripData.accommodations : [])
        .filter((a) => hasCoords(a === null || a === void 0 ? void 0 : a.location))
        .map((a) => {
        var _a;
        const loc = { latitude: a.location.latitude, longitude: a.location.longitude };
        const stop = (a.pinId && stopById.get(String(a.pinId))) || nearestStop(loc);
        const inMs = new Date(a.checkInDate).getTime();
        const outMs = new Date(a.checkOutDate).getTime();
        return {
            id: String(a.id),
            name: a.location.name || shortPlaceName(a.location.description) || 'Stay',
            ...loc,
            stopId: (_a = stop === null || stop === void 0 ? void 0 : stop.id) !== null && _a !== void 0 ? _a : null,
            photoUrl: a.location.photoUrl || null,
            nights: isNaN(inMs) || isNaN(outMs) ? null : Math.max(0, Math.round((outMs - inMs) / 86400000)),
        };
    });
    accommodations.forEach((a) => { var _a; if (a.stopId)
        (_a = stopById.get(a.stopId)) === null || _a === void 0 ? void 0 : _a.accommodationIds.push(a.id); });
    // Places carry their destination (a city place id), not a stop: match it
    // to a stay's place id, else the nearest stop. Visited first, by rank.
    const stopByPlaceId = new Map();
    const rawPins = Array.isArray(tripData === null || tripData === void 0 ? void 0 : tripData.pins) ? tripData.pins : [];
    rawPins.forEach((p) => {
        const stop = (p === null || p === void 0 ? void 0 : p.placeId) ? stopById.get(String(p.id)) : undefined;
        if (stop)
            stopByPlaceId.set(String(p.placeId), stop);
    });
    const toneOrder = { loved: 0, liked: 1, visited: 2, want: 3 };
    const places = savedPlaces
        .filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number')
        .map((p) => {
        var _a, _b;
        const loc = { latitude: p.lat, longitude: p.lng };
        const stop = (p.destinationPlaceId && stopByPlaceId.get(p.destinationPlaceId))
            || (p.cityPlaceId && stopByPlaceId.get(p.cityPlaceId))
            || nearestStop(loc);
        const tone = p.status !== 'visited'
            ? 'want'
            : p.rank === 'loved' || p.rank === 'liked' ? p.rank : 'visited';
        return {
            id: p.googlePlaceId,
            name: p.name,
            ...loc,
            thumbnailUrl: p.thumbnailUrl || null,
            types: (_a = p.types) !== null && _a !== void 0 ? _a : [],
            tone,
            stopId: (_b = stop === null || stop === void 0 ? void 0 : stop.id) !== null && _b !== void 0 ? _b : null,
        };
    })
        .sort((a, b) => toneOrder[a.tone] - toneOrder[b.tone]);
    places.forEach((p) => { var _a; if (p.stopId)
        (_a = stopById.get(p.stopId)) === null || _a === void 0 ? void 0 : _a.placeIds.push(p.id); });
    const dayVisits = pins
        .filter((p) => p.type === 'dayVisit' && hasCoords(p))
        .map((p) => {
        var _a, _b;
        const date = new Date(p.checkInDate);
        const parentId = (_a = dayVisitAssociations.get(p.id)) !== null && _a !== void 0 ? _a : null;
        if (parentId)
            (_b = stopById.get(parentId)) === null || _b === void 0 ? void 0 : _b.dayVisitIds.push(p.id);
        return {
            id: p.id,
            name: p.name || 'Day trip',
            latitude: p.latitude,
            longitude: p.longitude,
            date,
            day: tripDay(date, tripStart),
            parentId,
        };
    })
        .sort((a, b) => a.date.getTime() - b.date.getTime());
    const legTime = (part) => {
        const date = parseYmd(part === null || part === void 0 ? void 0 : part.date);
        return {
            date,
            day: date ? tripDay(date, tripStart) : null,
            time: typeof (part === null || part === void 0 ? void 0 : part.time) === 'string' && part.time ? part.time : null,
            utc: typeof (part === null || part === void 0 ? void 0 : part.utc) === 'string' ? part.utc : null,
        };
    };
    const legs = (Array.isArray(tripData === null || tripData === void 0 ? void 0 : tripData.commutes) ? tripData.commutes : [])
        .filter((c) => hasCoords(c === null || c === void 0 ? void 0 : c.departureLocation) && hasCoords(c === null || c === void 0 ? void 0 : c.arrivalLocation))
        .map((c) => {
        const mode = MODES.includes(c.commuteType) ? c.commuteType : 'flight';
        const fromLL = [c.departureLocation.longitude, c.departureLocation.latitude];
        const toLL = [c.arrivalLocation.longitude, c.arrivalLocation.latitude];
        // Flights fly the great circle; ground and sea legs stay straight.
        const coordinates = mode === 'flight' ? greatCircle(fromLL, toLL) : [fromLL, toLL];
        const mid = Math.min(Math.floor(coordinates.length / 2), coordinates.length - 2);
        const midpoint = mode === 'flight'
            ? coordinates[mid]
            : [(fromLL[0] + toLL[0]) / 2, (fromLL[1] + toLL[1]) / 2];
        // A great circle's heading changes along the way; take it where the badge sits.
        const heading = mode === 'flight' ? bearing(coordinates[mid], coordinates[mid + 1]) : bearing(fromLL, toLL);
        const departure = legTime(c.departureDate);
        const arrival = legTime(c.arrivalDate);
        const depMs = instantMs(departure);
        const arrMs = instantMs(arrival);
        const durationMin = depMs != null && arrMs != null && arrMs > depMs ? Math.round((arrMs - depMs) / 60000) : null;
        return {
            id: String(c.id),
            mode,
            role: 'route',
            from: {
                name: shortPlaceName(c.departureLocation.name),
                latitude: fromLL[1],
                longitude: fromLL[0],
                code: c.departureAirportCode || null,
            },
            to: {
                name: shortPlaceName(c.arrivalLocation.name),
                latitude: toLL[1],
                longitude: toLL[0],
                code: c.arrivalAirportCode || null,
            },
            departure,
            arrival,
            durationMin,
            fromStopId: c.fromDayVisitId || c.fromPinId || null,
            toStopId: c.toDayVisitId || c.toPinId || null,
            coordinates,
            midpoint,
            heading,
        };
    })
        .sort((a, b) => {
        var _a, _b, _c, _d, _e, _f;
        return ((_b = (_a = a.departure.date) === null || _a === void 0 ? void 0 : _a.getTime()) !== null && _b !== void 0 ? _b : 0) - ((_d = (_c = b.departure.date) === null || _c === void 0 ? void 0 : _c.getTime()) !== null && _d !== void 0 ? _d : 0)
            || ((_e = a.departure.time) !== null && _e !== void 0 ? _e : '').localeCompare((_f = b.departure.time) !== null && _f !== void 0 ? _f : '');
    });
    // Legs sharing both ends (a return leg, or two options for one hop)
    // would draw on top of each other. Bow each to its own right: opposite
    // directions land on opposite sides, same-direction repeats stack
    // outwards. A leg with no twin keeps its plain path.
    const sameEnd = (p, q) => Math.abs(p.latitude - q.latitude) < 0.3 && Math.abs(p.longitude - q.longitude) < 0.3;
    legs.forEach((leg, i) => {
        const twins = legs.filter((o) => (sameEnd(o.from, leg.from) && sameEnd(o.to, leg.to))
            || (sameEnd(o.from, leg.to) && sameEnd(o.to, leg.from)));
        if (twins.length < 2)
            return;
        const rank = legs.slice(0, i).filter((o) => sameEnd(o.from, leg.from) && sameEnd(o.to, leg.to)).length;
        leg.coordinates = bendPath(leg.coordinates, BEND * (rank + 1));
        const mid = Math.floor(leg.coordinates.length / 2);
        leg.midpoint = leg.coordinates[mid];
        leg.heading = bearing(leg.coordinates[mid], leg.coordinates[mid + 1]);
    });
    // Pairs of itinerary ids a commute already connects: each linked leg, plus
    // each multi-leg journey's origin → destination (a layover stop that isn't
    // on the itinerary leaves its legs half-linked). Day visits a commute
    // reaches are on the way: the legs draw them, so no side-trip line.
    const commutes = Array.isArray(tripData === null || tripData === void 0 ? void 0 : tripData.commutes) ? tripData.commutes : [];
    const coveredPairs = new Set();
    const onJourneyDayVisits = new Set();
    const journeys = new Map();
    commutes.forEach((c, i) => {
        var _a, _b;
        const from = (c === null || c === void 0 ? void 0 : c.fromDayVisitId) || (c === null || c === void 0 ? void 0 : c.fromPinId);
        const to = (c === null || c === void 0 ? void 0 : c.toDayVisitId) || (c === null || c === void 0 ? void 0 : c.toPinId);
        if (from && to)
            coveredPairs.add(`${from}__${to}`);
        if (c === null || c === void 0 ? void 0 : c.fromDayVisitId)
            onJourneyDayVisits.add(String(c.fromDayVisitId));
        if (c === null || c === void 0 ? void 0 : c.toDayVisitId)
            onJourneyDayVisits.add(String(c.toDayVisitId));
        const group = (c === null || c === void 0 ? void 0 : c.segmentGroupId) || `__leg_${(_a = c === null || c === void 0 ? void 0 : c.id) !== null && _a !== void 0 ? _a : i}`;
        journeys.set(group, [...((_b = journeys.get(group)) !== null && _b !== void 0 ? _b : []), c]);
    });
    const unlinkedEnds = [];
    const orderedJourneys = [];
    journeys.forEach((group) => {
        const ordered = [...group].sort((x, y) => { var _a, _b; return ((_a = x.legIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = y.legIndex) !== null && _b !== void 0 ? _b : 0); });
        orderedJourneys.push(ordered);
        const first = ordered[0];
        const last = ordered[ordered.length - 1];
        const from = (first === null || first === void 0 ? void 0 : first.fromDayVisitId) || (first === null || first === void 0 ? void 0 : first.fromPinId);
        const to = (last === null || last === void 0 ? void 0 : last.toDayVisitId) || (last === null || last === void 0 ? void 0 : last.toPinId);
        if (from && to)
            coveredPairs.add(`${from}__${to}`);
        const linked = ordered.some((c) => (c === null || c === void 0 ? void 0 : c.fromPinId) || (c === null || c === void 0 ? void 0 : c.toPinId) || (c === null || c === void 0 ? void 0 : c.fromDayVisitId) || (c === null || c === void 0 ? void 0 : c.toDayVisitId));
        if (!linked && hasCoords(first === null || first === void 0 ? void 0 : first.departureLocation) && hasCoords(last === null || last === void 0 ? void 0 : last.arrivalLocation)) {
            unlinkedEnds.push({ from: first.departureLocation, to: last.arrivalLocation });
        }
    });
    const near = (a, b) => {
        const rad = Math.PI / 180;
        const dLat = (b.latitude - a.latitude) * rad;
        const dLng = (b.longitude - a.longitude) * rad;
        const h = Math.sin(dLat / 2) ** 2
            + Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(dLng / 2) ** 2;
        return 2 * 6371 * Math.asin(Math.sqrt(h)) < 35;
    };
    // Getting there / heading home. A linked journey says so itself: it fills
    // the trip-start slot (no origin stop) or the trip-end slot (no
    // destination stop). An unlinked one is recognised by shape: it ends at
    // the first stop coming from outside the trip, or leaves the last stop
    // for somewhere outside it. Same itinerary, different home: these stay
    // muted and out of the default framing.
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];
    const nearAnyStop = (p) => stops.some((st) => near(p, st));
    const roleById = new Map();
    const outsidePlaces = [];
    orderedJourneys.forEach((ordered) => {
        const first = ordered[0];
        const last = ordered[ordered.length - 1];
        if (!firstStop || !hasCoords(first === null || first === void 0 ? void 0 : first.departureLocation) || !hasCoords(last === null || last === void 0 ? void 0 : last.arrivalLocation))
            return;
        const fromLinked = !!((first === null || first === void 0 ? void 0 : first.fromPinId) || (first === null || first === void 0 ? void 0 : first.fromDayVisitId));
        const toLinked = !!((last === null || last === void 0 ? void 0 : last.toPinId) || (last === null || last === void 0 ? void 0 : last.toDayVisitId));
        const linked = ordered.some((c) => (c === null || c === void 0 ? void 0 : c.fromPinId) || (c === null || c === void 0 ? void 0 : c.toPinId) || (c === null || c === void 0 ? void 0 : c.fromDayVisitId) || (c === null || c === void 0 ? void 0 : c.toDayVisitId));
        let role = 'route';
        if (linked) {
            if (!fromLinked && toLinked)
                role = 'arrival';
            else if (fromLinked && !toLinked)
                role = 'departure';
        }
        else if (near(last.arrivalLocation, firstStop) && !nearAnyStop(first.departureLocation)) {
            role = 'arrival';
        }
        else if (near(first.departureLocation, lastStop) && !nearAnyStop(last.arrivalLocation)) {
            role = 'departure';
        }
        if (role === 'route')
            return;
        ordered.forEach((c) => roleById.set(String(c.id), role));
        const outside = role === 'arrival' ? first : last;
        const loc = role === 'arrival' ? first.departureLocation : last.arrivalLocation;
        outsidePlaces.push({
            id: `outside-${outside.id}`,
            role,
            name: shortPlaceName(loc.name) || (role === 'arrival' ? 'Origin' : 'Destination'),
            latitude: loc.latitude,
            longitude: loc.longitude,
            legId: String(outside.id),
        });
    });
    legs.forEach((l) => { var _a; l.role = (_a = roleById.get(l.id)) !== null && _a !== void 0 ? _a : 'route'; });
    const lines = buildRouteLines(pins, dayVisitAssociations, {
        isCovered: (from, to) => coveredPairs.has(`${from.id}__${to.id}`)
            || unlinkedEnds.some((j) => near(j.from, from) && near(j.to, to)),
        isOnJourney: (dayVisitId) => onJourneyDayVisits.has(dayVisitId),
    });
    // Frame the trip itself: stops and day visits. Journeys to and from home
    // still draw, but would otherwise shrink the route to a dot (Rio → Lisbon).
    const framed = [
        ...stops.map((st) => [st.longitude, st.latitude]),
        ...dayVisits.map((d) => [d.longitude, d.latitude]),
    ];
    const coords = framed.length
        ? framed
        : legs.flatMap((l) => [l.coordinates[0], l.coordinates[l.coordinates.length - 1]]);
    const bounds = coords.length
        ? [
            [Math.min(...coords.map((c) => c[0])), Math.min(...coords.map((c) => c[1]))],
            [Math.max(...coords.map((c) => c[0])), Math.max(...coords.map((c) => c[1]))],
        ]
        : null;
    return { stops, dayVisits, legs, outsidePlaces, accommodations, places, lines, flexible, bounds };
}
